import { ReactNode } from "react";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface SetupCardAction {
  label: string;
  href: string;
  ariaLabel?: string;
}

interface SetupCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  primaryAction: SetupCardAction;
  secondaryAction?: SetupCardAction;
  children?: ReactNode;
  className?: string;
}

export function SetupCard({
  icon: Icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  children,
  className,
}: SetupCardProps) {
  return (
    <div className={cn("rounded-2xl border border-gray-200 bg-white p-6 shadow-sm", className)}>
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-100 text-orange-600">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="space-y-1">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
      </div>

      {children && <div className="mt-4 space-y-3 text-sm text-gray-600">{children}</div>}

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button asChild className="gap-2" aria-label={primaryAction.ariaLabel ?? primaryAction.label}>
          <a href={primaryAction.href} target="_blank" rel="noopener noreferrer">
            {primaryAction.label}
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </a>
        </Button>
        {secondaryAction && (
          <a
            href={secondaryAction.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-orange-600 hover:text-orange-700"
            aria-label={secondaryAction.ariaLabel ?? secondaryAction.label}
          >
            {secondaryAction.label}
          </a>
        )}
      </div>
    </div>
  );
}

export default SetupCard;
